import { Link } from "react-router-dom";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";
import { SubprocessorTable } from "../../components/SubprocessorTable";

/**
 * Privacy Policy.
 *
 * Known gaps — fill as they're resolved:
 * - Legal entity: INTAKALL LTD (England & Wales), reg. office 71-75 Shelton Street, London WC2H 9JQ
 * - Retention numbers (60-day Free / 1-year Pro transcripts, 30-day logs, 7-year billing)
 *   must match the nightly sweep (migration 014) and what the dashboard shows.
 * - DPO appointment — not legally required at current scale.
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white font-body text-slate-800">
      <Navbar />
      <main>

      <article className="mx-auto max-w-3xl px-6 py-12 leading-relaxed">
        <header className="mb-10 border-b border-slate-200 pb-6">
          <h1 className="text-4xl font-semibold text-slate-900">Privacy Policy</h1>
          <p className="mt-2 text-sm text-slate-500">
            Effective date: 2026-04-21
            <span className="mx-2">·</span>
            Last updated: 2026-10-25
          </p>
          <div className="mt-6 rounded border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
            <p className="font-semibold">We do not train AI models on your data.</p>
            <p className="mt-1">
              Your meeting audio, transcripts, and messages are never used to train our
              models or our subprocessors'. On Pro and Enterprise, we also use each vendor's
              settings that keep your content out of their model training. On the Free plan, our speech-to-text provider, Deepgram, may use meeting audio to improve its models.
            </p>
          </div>
        </header>

        <Section n="1" title="Who we are">
          <p>
            Intakall AI ("Intakall," "we," "us") is a meeting intelligence
            platform for teams. This policy explains what personal
            data we collect when you use the Intakall Chrome extension,
            dashboard, and related services (collectively, the "Service"), and
            how we handle that data.
          </p>
          <ul className="mt-4 list-none space-y-1 text-sm">
            <li>
              <span className="font-medium text-slate-900">Data controller:</span>{" "}
              INTAKALL LTD, a company registered in England and Wales, registered office
              71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom.
            </li>
            <li>
              <span className="font-medium text-slate-900">Contact:</span>{" "}
              <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">
                privacy@intakall.io
              </a>
            </li>
          </ul>
        </Section>

        <Section n="2" title="What personal data we collect">
          <h3 className="mt-6 text-lg font-medium text-slate-900">2.1 From you (when you sign up)</h3>
          <ul className="ml-6 list-disc space-y-1">
            <li>Google account identifiers (name, email, avatar URL, Google subject ID) — via OAuth.</li>
            <li>Team information you provide (team name, domain restrictions, invited member emails).</li>
          </ul>

          <h3 className="mt-6 text-lg font-medium text-slate-900">2.2 From your integrations</h3>
          <ul className="ml-6 list-disc space-y-1">
            <li>
              <strong>GitHub:</strong> your identity, an access token (encrypted at rest), and the
              repositories you grant us access to read.
            </li>
            <li>
              <strong>Linear:</strong> your identity, an access token (encrypted at rest), default team
              ID, and issues you grant us access to read.
            </li>
            <li>
              <strong>Jira:</strong> your identity, an access token (encrypted at rest), and the
              projects and issues you grant us access to read, plus tickets you create.
            </li>
            <li>
              <strong>Slack:</strong> an access token (encrypted at rest) and messages from the
              channels you choose.
            </li>
            <li>
              <strong>Zoom:</strong> your Zoom user ID and an access token (encrypted at rest), so
              Intakall can work inside your Zoom meetings. If you remove Intakall from Zoom, we
              delete these straight away.
            </li>
          </ul>

          <h3 className="mt-6 text-lg font-medium text-slate-900">2.3 During meetings</h3>
          <ul className="ml-6 list-disc space-y-1">
            <li>
              <strong>Audio:</strong> the audio of a Google Meet call you choose to record — both
              your microphone and the other participants' audio from the meeting tab — mixed locally
              and streamed to our transcription provider (Deepgram) in real time. Recording starts
              only when you explicitly start it, and you are responsible for obtaining the other
              participants' consent to record.{" "}
              <strong>Intakall does not store audio at any point.</strong>
            </li>
            <li>
              <strong>Zoom audio:</strong> in a Zoom meeting, when you press Start in the Intakall app,
              Zoom sends us the meeting's audio through Zoom's real-time media streams, separated by
              participant, and we stream it to Deepgram in real time. As above, you are responsible for obtaining the other
              participants' consent, and Intakall does not store the audio.
            </li>
            <li>
              <strong>Transcripts:</strong> text transcriptions returned by Deepgram, with speaker
              name where available and timestamps.
            </li>
            <li>
              <strong>Meeting metadata:</strong> meeting URL, start/end time, participant display
              names (derived from Google Meet's DOM, or supplied by Zoom in a Zoom meeting), the
              Zoom meeting ID for Zoom meetings, team association.
            </li>
            <li>
              <strong>Derived cards:</strong> the pull requests, issues, and tickets surfaced in the
              sidebar during your meeting.
            </li>
          </ul>

          <h3 className="mt-6 text-lg font-medium text-slate-900">2.4 Automatically</h3>
          <ul className="ml-6 list-disc space-y-1">
            <li>Error and performance data via Sentry; PII scrubbed where possible.</li>
            <li>Server logs on our hosting provider (Fly.io), retained for 30 days.</li>
            <li>
              <strong>Product step records:</strong> which steps you reached in the extension and the
              dashboard (for example installed, signed in, pressed Start, opened a summary, opened
              billing), with a random install ID, the extension version, and error codes. Never what is
              said or typed. These records stay on our own servers and are not shared with any
              analytics company.
            </li>
            <li>
              <strong>Video pages we send:</strong> when a video page we emailed to your team is opened,
              we record that it was opened, how far its video was watched, whether the sound was turned
              on and whether a call was booked, with the team name in the link. Nothing else about you.
            </li>
            <li>Billing data via Stripe (we do not see full card numbers).</li>
          </ul>
        </Section>

        <Section n="3" title="Why we process your data">
          <table className="mt-4 w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                <th className="py-2 pr-4 font-medium text-slate-900">Purpose</th>
                <th className="py-2 font-medium text-slate-900">Legal basis (GDPR)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <tr>
                <td className="py-2 pr-4">Providing the Service (transcription, extraction, cards)</td>
                <td className="py-2">Performance of a contract</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Authenticating you and enforcing team membership</td>
                <td className="py-2">Performance of a contract</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Charging you for paid plans</td>
                <td className="py-2">Performance of a contract</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Detecting abuse, fraud, and security incidents</td>
                <td className="py-2">Legitimate interest</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Improving the Service (aggregated, de-identified)</td>
                <td className="py-2">Legitimate interest</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Seeing where people get stuck, so we can fix it and help them (product step records)</td>
                <td className="py-2">Legitimate interest</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Responding to support requests</td>
                <td className="py-2">Legitimate interest</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Legal compliance</td>
                <td className="py-2">Legal obligation</td>
              </tr>
            </tbody>
          </table>

          <p className="mt-6 rounded border border-slate-200 bg-slate-50 p-4 text-sm">
            <strong>No AI model training.</strong> We do not use your meeting audio or transcripts
            to train AI models of our own. On Pro and Enterprise, our subprocessors are configured to
            exclude your data from their model training. On the Free plan, our speech-to-text provider, Deepgram, may use meeting audio to improve its models.
          </p>
        </Section>

        <Section n="4" title="Retention">
          <ul className="ml-6 list-disc space-y-1">
            <li><strong>Audio:</strong> never stored.</li>
            <li>
              <strong>Transcripts and summaries:</strong> 60 days on Free plans, 1 year on Pro
              plans. A nightly job deletes anything past that automatically. On Enterprise plans,
              transcripts are kept until you delete them, or never stored at all with zero-retention
              mode. Sidebar cards are shown live and are not stored. You can also delete
              individual meetings from the dashboard at any time, or request bulk deletion by
              emailing privacy@intakall.io.
            </li>
            <li>
              <strong>Tickets you created from meetings (Linear or Jira):</strong> kept forever (a record of
              work done). The transcript that informed each ticket may be gone, but the ticket
              record remains.
            </li>
            <li>
              <strong>Integration tokens:</strong> retained until you disconnect the integration or
              delete your account. Your Zoom connection and token are deleted as soon as you remove
              Intakall from Zoom.
            </li>
            <li>
              <strong>Billing records:</strong> retained for 7 years after the last transaction
              (statutory retention for tax records).
            </li>
            <li>
              <strong>Error and log data:</strong> 30-day rolling window.
            </li>
            <li>
              <strong>Product step and event records:</strong> 90 days, then deleted automatically.
            </li>
          </ul>
          <p className="mt-4">You can request deletion of your transcripts and account at any time (Section 7).</p>
        </Section>

        <Section n="5" title="Who we share your data with">
          <h3 className="mt-6 text-lg font-medium text-slate-900">5.1 Subprocessors</h3>
          <p className="text-sm text-slate-600">
            An always-current list with change-notification policy lives at{" "}
            <Link to="/subprocessors" className="text-indigo-600 hover:underline">
              /subprocessors
            </Link>
            . The snapshot below is current as of the "Last updated" date at the top of
            this page.
          </p>
          <div className="mt-3 overflow-x-auto">
            <SubprocessorTable />
          </div>

          <p className="mt-4">
            We do not sell your personal data. We do not share it with advertisers.
          </p>

          <h3 className="mt-6 text-lg font-medium text-slate-900">5.2 Legal disclosures</h3>
          <p>
            We may disclose your data if required to do so by law, subpoena, court order, or where
            necessary to protect the rights, property, or safety of Intakall, our users, or the
            public.
          </p>
        </Section>

        <Section n="6" title="International transfers">
          <p>
            Your data is primarily processed in the United Kingdom and the European Economic
            Area: our server runs in London (Fly.io), our database is in Ireland (Supabase),
            and our cache is in the EU (Upstash). Where data leaves the EEA or the UK — most
            notably for speech-to-text (Deepgram), topic extraction and summaries (OpenAI), and
            billing (Stripe) — we rely on Standard Contractual Clauses and equivalent
            safeguards from each subprocessor.
          </p>
        </Section>

        <Section n="7" title="Your rights">
          <p>Depending on your jurisdiction, you may have the right to:</p>
          <ul className="ml-6 mt-3 list-disc space-y-1">
            <li><strong>Access</strong> — request a copy of the personal data we hold about you.</li>
            <li><strong>Rectify</strong> — correct inaccurate data.</li>
            <li><strong>Delete</strong> — request deletion of your account and associated data. Self-serve deletion from the dashboard is on the roadmap; for now, see the email instructions below.</li>
            <li><strong>Restrict</strong> — restrict processing while a dispute is being resolved.</li>
            <li><strong>Portability</strong> — receive your data in a structured, machine-readable format.</li>
            <li><strong>Object</strong> — object to processing based on legitimate interests.</li>
            <li><strong>Withdraw consent</strong> — where we rely on consent as the legal basis.</li>
          </ul>
          <p className="mt-4">
            To exercise these rights, email{" "}
            <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">
              privacy@intakall.io
            </a>
            . We will respond within 30 days. If you are in the EEA or UK, you also have the right
            to lodge a complaint with your local supervisory authority.
          </p>
        </Section>

        <Section n="8" title="Security">
          <p>
            We take security seriously. See our{" "}
            <Link to="/security" className="text-indigo-600 hover:underline">
              Security Trust Centre
            </Link>{" "}
            for full details. In brief: data is encrypted in transit (TLS 1.3) and at rest
            (AES-256); integration tokens are encrypted per-user with a key derived from a secret
            master key held only in host secrets; access is governed by Supabase Row-Level
            Security.
          </p>
          <p className="mt-3">
            Despite our safeguards, no method of transmission over the internet is 100% secure. In
            the event of a breach that affects your data, we will notify you and the relevant
            supervisory authority within 72 hours of becoming aware, in accordance with applicable
            law.
          </p>
        </Section>

        <Section n="9" title="US state privacy rights (CCPA, CPRA, VCDPA, CPA, CTDPA, UCPA)">
          <p>
            If you are a resident of California, Virginia, Colorado, Connecticut, Utah, or
            another US state with a comprehensive privacy law, you have additional rights
            regardless of whether we operate in your state.
          </p>

          <h3 className="mt-6 text-lg font-medium text-slate-900">9.1 Your rights</h3>
          <ul className="ml-6 list-disc space-y-1">
            <li><strong>Right to know</strong> — what personal information we collect, use, and disclose.</li>
            <li><strong>Right to access</strong> — a copy of the personal information we hold about you.</li>
            <li><strong>Right to delete</strong> — ask us to delete your personal information.</li>
            <li><strong>Right to correct</strong> — rectify inaccurate personal information.</li>
            <li><strong>Right to opt out of sale or sharing</strong> (see below).</li>
            <li><strong>Right to limit use of sensitive personal information</strong> — we do not use sensitive PI for any purpose beyond providing the Service.</li>
            <li><strong>Right to non-discrimination</strong> — exercising your rights will not result in worse pricing, worse service, or denial of goods.</li>
          </ul>

          <h3 className="mt-6 text-lg font-medium text-slate-900">9.2 Do Not Sell or Share My Personal Information</h3>
          <p className="rounded border border-slate-200 bg-slate-50 p-4">
            <strong>Intakall does not sell your personal information,</strong> and we do not
            share it for cross-context behavioural advertising. This has been true since
            day one and is a commitment we do not intend to change. If our practices ever
            change, we will notify you and honour any opt-out you have previously given.
          </p>

          <h3 className="mt-6 text-lg font-medium text-slate-900">9.3 How to exercise your rights</h3>
          <p>
            Email{" "}
            <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">
              privacy@intakall.io
            </a>
            {" "}with the request and a way to verify your identity (the email address
            associated with your Intakall account is usually enough). We respond within
            45 days per CCPA / within 30 days per other state laws.
          </p>
          <p className="mt-3">
            You may also use an authorised agent. We will require written permission and
            reasonable proof that the agent is acting on your behalf.
          </p>
          <p className="mt-3">
            We recognise the Global Privacy Control (GPC) signal as a valid opt-out where
            applicable.
          </p>
        </Section>

        <Section n="10" title="Children">
          <p>
            Intakall is built for workplace use and is not directed at children.
          </p>
          <ul className="ml-6 mt-3 list-disc space-y-1">
            <li>
              <strong>US (COPPA):</strong> we do not knowingly collect personal information
              from children under 13. If you believe a child has provided us with data,
              email{" "}
              <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">
                privacy@intakall.io
              </a>
              {" "}and we will delete it.
            </li>
            <li>
              <strong>EU/UK (GDPR Article 8):</strong> we do not knowingly process the
              personal data of anyone under 16 without verifiable parental consent. Some
              EU member states set this age lower (13–15) — we observe whichever is higher.
            </li>
            <li>
              <strong>Service terms:</strong> use of Intakall by anyone under 18 is
              prohibited by our Terms of Service regardless of local privacy law minimums.
            </li>
          </ul>
        </Section>

        <Section n="11" title="Cookies">
          <p>
            Our dashboard uses a small number of strictly necessary cookies for authentication
            (Supabase session) and to remember your preferences. These are required for the
            Service to work and cannot be turned off.
          </p>
          <p>
            Our public website uses the Meta pixel, an analytics and advertising cookie provided
            by Meta Platforms Ireland Limited. It tells us how people arrive at our site and lets
            us show ads to people who have visited us before. It is set only if you accept it
            when asked, it is never set on the dashboard, and declining has no effect on your use
            of the Service. If you decline, no pixel is loaded and no data is sent to Meta.
          </p>
          <p>
            You can change your mind at any time by clearing this site's cookies and site data in
            your browser, which will cause us to ask you again on your next visit. Meta processes
            this data as an independent controller under its own{" "}
            <a
              href="https://www.facebook.com/privacy/policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 underline underline-offset-2 hover:text-brand-700"
            >
              privacy policy
            </a>
            .
          </p>
        </Section>

        <Section n="12" title="Changes to this policy">
          <p>
            We may update this policy from time to time. Material changes will be communicated by
            email to the address associated with your account and noted at the top of this page.
            Continued use of the Service after a change constitutes acceptance of the updated
            policy.
          </p>
        </Section>

        <Section n="13" title="Contact">
          <ul className="ml-6 list-disc space-y-1">
            <li>
              <strong>Privacy questions:</strong>{" "}
              <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">
                privacy@intakall.io
              </a>
            </li>
            <li>
              <strong>Security issues:</strong>{" "}
              <a href="mailto:security@intakall.io" className="text-indigo-600 hover:underline">
                security@intakall.io
              </a>{" "}
              — see our{" "}
              <Link to="/security" className="text-indigo-600 hover:underline">
                Security Trust Centre
              </Link>
              .
            </li>
          </ul>
        </Section>

        <footer className="mt-16 border-t border-slate-200 pt-6 text-sm text-slate-500">
          <Link to="/" className="text-indigo-600 hover:underline">
            ← Back to Intakall
          </Link>
        </footer>
      </article>
      </main>
      <Footer />
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-semibold text-slate-900">
        <span className="mr-3 text-slate-400">{n}.</span>
        {title}
      </h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}


